import { useMergeContext } from '../../../store/mergeContext';
import { useLingui } from '@lingui/react';
import { useTheme } from '@emotion/react';
import {
  Box,
  Stack,
  Paper,
  Typography,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  TextField,
} from
'@mui/material';
import { BASIC_ORG } from '../merge-constants';
import { gridClasses } from '@mui/x-data-grid';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Trans, msg } from '@lingui/macro';
import {
  COLLECTED_BY_ID,
  CRASH_ID_KEY,
  DATE_ID_KEY,
  PEOPLE_ID_KEY,
  UNIT_ID_KEY,
  getColumnsModel,
  getOrganization } from
'../utils';
import { getConfigOptions, mapObjectToLabels } from '../../Statistics/utils';
import { formatDateForList } from '../../../utils';
import { StripedDataGrid } from "../../../components/tables/StripedDataGrid";

const SOURCE_OPTIONS = [
{ basic: BASIC_ORG.POLICE, msgKey: 'police' },
{ basic: BASIC_ORG.HOSPITAL, msgKey: 'hospital' },
{ basic: BASIC_ORG.INSURANCE, msgKey: 'insurance' }];

const DAYS_MIN = 1;
const DAYS_MAX = 3650;
const DAYS_DEBOUNCE_MS = 300;

function countUnits(crash) {
  return Object.entries(crash?.units || {})?.length || 0;
}

function countPeople(crash) {
  const unitPeople =
    Object.values(crash?.units || {})?.flatMap((v) =>
      Object.values(v?.people || {})?.flatMap((p) => p)
    )?.length || 0;
  const crashPeople = Object.values(crash?.people || {})?.length || 0;
  return unitPeople + crashPeople;
}

/** Map API crash payloads to CrashTable rows (supports format=list counts). */
function mapCrashToTableRow(crash, configOptions, organizations, index) {
  const mapped = mapObjectToLabels(crash, configOptions);
  const crashPart = mapped?.crash || crash?.crash || {};
  const unitCount =
    crash?.unit_count ??
    mapped?.unit_count ??
    countUnits(mapped);
  const peopleCount =
    crash?.people_count ??
    mapped?.people_count ??
    countPeople(mapped);
  return {
    id: index,
    ...crashPart,
    [UNIT_ID_KEY]: unitCount,
    [PEOPLE_ID_KEY]: peopleCount,
    [COLLECTED_BY_ID]: getOrganization(
      crash?.crash?.[COLLECTED_BY_ID] ?? crashPart?.[COLLECTED_BY_ID],
      organizations
    ),
    merge_date: crash?.crash?.merge_date ?? crash?.merge_date ?? crashPart?.merge_date,
  };
}

export const CrashTable = ({ isLoadingCrashes = false }) => {
  const { _ } = useLingui();
  const {
    crashes,
    handleSelectedCrash,
    mergedCrashes,
    sourceBasicOrganization,
    setSourceBasicOrganization,
    daysFilter,
    setDaysFilter,
  } = useMergeContext();

  const [columns, setColumns] = useState([]);
  const [columnGroupingModel, setColumnGroupingModel] = useState([]);
  const [rows, setRows] = useState([]);
  const [validatedRow, setValidatedRows] = useState([]);
  const [daysInput, setDaysInput] = useState(String(daysFilter));
  const daysDebounceRef = useRef(null);
  const location = useLocation();
  const theme = useTheme();
  const init = useMemo(
    () => location.state?.init || {},
    [location.state?.init]
  );
  const organizations = useMemo(
    () => location.state?.init?.organizations || [],
    [location.state?.init?.organizations]
  );

  const sourceOrgLabels = useMemo(
    () => ({
      [BASIC_ORG.POLICE]: _(msg`Police`),
      [BASIC_ORG.HOSPITAL]: _(msg`Hospital`),
      [BASIC_ORG.INSURANCE]: _(msg`Insurance`)
    }),
    [_]
  );
  const dateFormatter = useCallback(
    (paramsOrValue) => formatDateForList(paramsOrValue?.value ?? paramsOrValue),
    []
  );

  const columnsWithDateFormatter = useMemo(
    () =>
    columns.map((col) =>
    col.field === DATE_ID_KEY || /\bdate\b/i.test(col.field ?? '') ?
    { ...col, valueFormatter: dateFormatter } :
    col
    ),
    [columns, dateFormatter]
  );

  const validatedColumns = useMemo(
    () => [
    ...columnsWithDateFormatter,
    {
      field: 'merge_date',
      headerName: _(msg`Validation date`),
      headerClassName: 'overload-header',
      rowSpanValueGetter: () => null,
      flex: 1,
      valueFormatter: dateFormatter
    }],

    [columnsWithDateFormatter, _, dateFormatter]
  );

  const applyDaysFilter = useCallback((raw) => {
    const parsed = Number.parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(parsed) || parsed < DAYS_MIN) {
      setDaysInput(String(daysFilter));
      return;
    }
    const next = Math.min(DAYS_MAX, parsed);
    setDaysInput(String(next));
    if (next !== daysFilter) setDaysFilter(next);
  }, [daysFilter, setDaysFilter]);

  const handleDaysChange = useCallback((event) => {
    const value = event.target.value;
    setDaysInput(value);
    if (daysDebounceRef.current) clearTimeout(daysDebounceRef.current);
    daysDebounceRef.current = setTimeout(() => {
      applyDaysFilter(value);
    }, DAYS_DEBOUNCE_MS);
  }, [applyDaysFilter]);

  useEffect(() => () => {
    if (daysDebounceRef.current) clearTimeout(daysDebounceRef.current);
  }, []);

  useEffect(() => {
    setDaysInput(String(daysFilter));
  }, [daysFilter]);

  useEffect(() => {
    const { configuration } = init;
    const configOptions = getConfigOptions(configuration);
    setValidatedRows(
      (mergedCrashes ?? []).map((crash, index) =>
        mapCrashToTableRow(crash, configOptions, organizations, index)
      )
    );
  }, [init, mergedCrashes, organizations]);

  useEffect(() => {
    const { configuration } = init;
    const configOptions = getConfigOptions(configuration);
    setRows(
      (crashes ?? []).map((crash, index) =>
        mapCrashToTableRow(crash, configOptions, organizations, index)
      )
    );
    const { columns: nextColumns } = getColumnsModel(configuration, _);
    setColumnGroupingModel();
    setColumns(nextColumns);
  }, [init, crashes, organizations, _]);

  return (
    <Stack direction="column">
      <Box sx={{ p: 1, borderBottom: `1px solid ${theme.palette.newColors.gray}` }}>
        <Typography sx={{ color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : theme.palette.newColors.lightGray, fontWeight: 'bold', fontSize: 24 }}>{_(msg`Data merging and validation`)}</Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: 1, padding: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography sx={{ color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white', fontWeight: 'bold', fontSize: 18, ml: -2 }} variant="h3">
          <Trans>SELECT A CRASH TO VALIDATE</Trans>
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            gap: 3,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
            <Typography
              sx={{
                color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white',
                fontSize: 14,
                lineHeight: 1.2,
              }}
            >
              <Trans>Last</Trans>
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, minHeight: 40 }}>
              <TextField
                size="small"
                type="number"
                value={daysInput}
                onChange={handleDaysChange}
                onBlur={() => applyDaysFilter(daysInput)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyDaysFilter(daysInput);
                  }
                }}
                inputProps={{ min: DAYS_MIN, max: DAYS_MAX, step: 1, 'aria-label': _(msg`days`) }}
                sx={{ width: 88 }}
                disabled={isLoadingCrashes}
              />
              <Typography
                sx={{
                  color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white',
                  fontSize: 14,
                }}
              >
                <Trans>days</Trans>
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
            <Typography
              sx={{
                color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white',
                fontSize: 14,
                lineHeight: 1.2,
              }}
            >
              <Trans>Source organization</Trans>
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={sourceBasicOrganization}
              disabled={isLoadingCrashes}
              onChange={(_e, value) => {
                if (value != null) setSourceBasicOrganization(value);
              }}
              sx={{ minHeight: 40 }}
            >
              {SOURCE_OPTIONS.map(({ basic }) =>
                <ToggleButton key={basic} value={basic}>
                  {sourceOrgLabels[basic]}
                </ToggleButton>
              )}
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Box>
      <Paper sx={{ mb: 2, position: 'relative', minHeight: 200 }}>
        {isLoadingCrashes &&
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (t) =>
            t.palette.mode === 'light' ?
            'rgba(255, 255, 255, 0.72)' :
            'rgba(0, 0, 0, 0.45)'
          }}
          aria-busy="true"
          aria-live="polite">
          
            <CircularProgress size={40} />
          </Box>
        }
        <StripedDataGrid
          rowSelection
          onRowClick={(e) => !isLoadingCrashes && handleSelectedCrash(e?.row?.[CRASH_ID_KEY])}
          showColumnVerticalBorder
          showCellVerticalBorder
          rows={rows}
          columns={columnsWithDateFormatter}
          editMode="row"
          columnGroupingModel={columnGroupingModel}
          pageSizeOptions={[5]}
          sx={{
            border: 0,
            opacity: isLoadingCrashes ? 0.5 : 1,
            pointerEvents: isLoadingCrashes ? 'none' : 'auto',
            [`& .${gridClasses.menuIcon} > *`]: {
              color: theme.palette.primary.contrastText
            }
          }} />
        
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: 1, padding: 2 }}>
        <Typography sx={{ color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white', fontWeight: 'bold', fontSize: 18, ml: -2 }} variant="h3">
          <Trans>VALIDATED CRASHES</Trans>
        </Typography>

      </Box>
      <Divider sx={{ mb: 2 }} />
      <Paper sx={{ pt: -1, mb: 2 }}>
        <StripedDataGrid
          rowSelection={false}
          showColumnVerticalBorder
          showCellVerticalBorder
          rows={validatedRow}
          columns={validatedColumns}
          editMode="row"
          columnGroupingModel={columnGroupingModel}
          pageSizeOptions={[5]}
          sx={{
            border: 0,
            [`& .${gridClasses.menuIcon} > *`]: {
              color: theme.palette.primary.contrastText
            }
          }} />
        
      </Paper>
    </Stack>);

};
